/**
 * Componente KPISelector
 * 
 * Seletor de KPIs para personalizar quais métricas são exibidas no dashboard.
 * Permite ao usuário escolher quais KPIs deseja visualizar.
 * 
 * @module components/Dashboard/DashboardFilters/KPISelector
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaCog, FaCheck, FaTimes } from 'react-icons/fa';
import kpiConfig from '../../../utils/dashboard/kpiConfig';

/**
 * Componente de seletor de KPIs
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array<string>} props.visibleKPIs - Array de chaves de KPIs visíveis
 * @param {function} props.onKPIToggle - Callback quando um KPI é alternado
 * @param {function} props.onReset - Callback para resetar seleção
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de seletor de KPIs
 */
const KPISelector = ({ visibleKPIs, onKPIToggle, onReset, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);

    const mainKPIs = kpiConfig.filter(kpi => kpi.category === 'main');
    const alertKPIs = kpiConfig.filter(kpi => kpi.category === 'alert');

    const handleToggle = (kpiKey) => {
        onKPIToggle(kpiKey);
    };

    const handleReset = () => {
        onReset();
        setIsOpen(false);
    };

    return (
        <div className={`relative ${className}`}>
            {/* Botão de abrir seletor */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="
                    flex items-center gap-2
                    px-3 sm:px-4 py-2 sm:py-2.5
                    text-xs sm:text-sm font-semibold
                    text-gray-700
                    bg-white
                    border border-gray-300
                    rounded-lg
                    shadow-sm
                    hover:border-emerald-400
                    hover:bg-gray-50
                    focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                    transition-colors duration-200
                "
                aria-label="Selecionar KPIs"
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <FaCog 
                    className="text-gray-500" 
                    size={14}
                    aria-hidden="true"
                />
                <span className="hidden sm:inline">KPIs</span>
            </button>

            {/* Dropdown de seleção */}
            {isOpen && (
                <>
                    {/* Overlay para fechar ao clicar fora */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsOpen(false)}
                        aria-hidden="true"
                    />
                    
                    {/* Menu dropdown */}
                    <div
                        className="
                            absolute right-0 mt-2 w-72 sm:w-80
                            bg-white
                            border border-gray-200
                            rounded-lg
                            shadow-xl
                            z-20
                            max-h-96 overflow-y-auto
                        "
                        role="menu"
                        aria-label="Selecionar KPIs para exibir"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-semibold text-gray-900">
                                    Selecionar KPIs
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="
                                        p-1
                                        text-gray-400
                                        hover:text-gray-600
                                        focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded
                                    "
                                    aria-label="Fechar seletor"
                                >
                                    <FaTimes size={14} aria-hidden="true" />
                                </button>
                            </div>
                            <p className="text-xs text-gray-500">
                                Selecione quais métricas deseja visualizar
                            </p>
                        </div>

                        {/* Lista de KPIs Principais */}
                        <div className="p-4">
                            <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                                Métricas Principais
                            </h4>
                            <div className="space-y-2">
                                {mainKPIs.map((kpi) => {
                                    const isVisible = visibleKPIs.includes(kpi.key);
                                    const Icon = kpi.icon;
                                    
                                    return (
                                        <label
                                            key={kpi.key}
                                            className="
                                                flex items-center gap-3
                                                p-2
                                                rounded-lg
                                                hover:bg-gray-50
                                                cursor-pointer
                                                transition-colors duration-150
                                            "
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isVisible}
                                                onChange={() => handleToggle(kpi.key)}
                                                className="
                                                    w-4 h-4
                                                    text-emerald-600
                                                    border-gray-300
                                                    rounded
                                                    focus:ring-emerald-500
                                                    cursor-pointer
                                                "
                                                aria-label={`${kpi.label} - ${isVisible ? 'visível' : 'oculto'}`}
                                            />
                                            <div className="flex items-center gap-2 flex-1">
                                                <div className={`p-1.5 rounded ${kpi.bg}`}>
                                                    <Icon 
                                                        className={kpi.color} 
                                                        size={14}
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                                <span className="text-sm text-gray-700">
                                                    {kpi.label}
                                                </span>
                                            </div>
                                            {isVisible && (
                                                <FaCheck 
                                                    className="text-emerald-600" 
                                                    size={12}
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Lista de KPIs de Alertas */}
                        {alertKPIs.length > 0 && (
                            <div className="p-4 pt-0 border-t border-gray-200">
                                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                                    Alertas
                                </h4>
                                <div className="space-y-2">
                                    {alertKPIs.map((kpi) => {
                                        const isVisible = visibleKPIs.includes(kpi.key);
                                        const Icon = kpi.icon;
                                        
                                        return (
                                            <label
                                                key={kpi.key}
                                                className="
                                                    flex items-center gap-3
                                                    p-2
                                                    rounded-lg
                                                    hover:bg-gray-50
                                                    cursor-pointer
                                                    transition-colors duration-150
                                                "
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isVisible}
                                                    onChange={() => handleToggle(kpi.key)}
                                                    className="
                                                        w-4 h-4
                                                        text-emerald-600
                                                        border-gray-300
                                                        rounded
                                                        focus:ring-emerald-500
                                                        cursor-pointer
                                                    "
                                                    aria-label={`${kpi.label} - ${isVisible ? 'visível' : 'oculto'}`}
                                                />
                                                <div className="flex items-center gap-2 flex-1">
                                                    <div className={`p-1.5 rounded ${kpi.bg}`}>
                                                        <Icon 
                                                            className={kpi.color} 
                                                            size={14}
                                                            aria-hidden="true"
                                                        />
                                                    </div>
                                                    <span className="text-sm text-gray-700">
                                                        {kpi.label}
                                                    </span>
                                                </div>
                                                {isVisible && (
                                                    <FaCheck 
                                                        className="text-emerald-600" 
                                                        size={12}
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Footer com botão de resetar */}
                        <div className="p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="
                                    w-full
                                    px-4 py-2
                                    text-sm font-medium
                                    text-gray-700
                                    bg-white
                                    border border-gray-300
                                    rounded-lg
                                    hover:bg-gray-50
                                    hover:border-gray-400
                                    focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
                                    transition-colors duration-200
                                "
                            >
                                Resetar para Padrão
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

KPISelector.propTypes = {
    /** Array de chaves de KPIs visíveis */
    visibleKPIs: PropTypes.arrayOf(PropTypes.string).isRequired,
    
    /** Callback quando um KPI é alternado */
    onKPIToggle: PropTypes.func.isRequired,
    
    /** Callback para resetar seleção */
    onReset: PropTypes.func.isRequired,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

KPISelector.defaultProps = {
    className: '',
};

export default KPISelector;

